import type { MediaRendition } from "./media-types";

export const STORAGE_MULTIPART_THRESHOLD_BYTES = 20 * 1024 * 1024;
export const STORAGE_MULTIPART_PART_BYTES = 10 * 1024 * 1024;

export interface SingleStorageUploadDestination {
  readonly contentType: string;
  readonly mediaId: string;
  readonly rendition: MediaRendition;
  readonly strategy: "single";
  readonly url: string;
}

export interface MultipartStorageUploadPart {
  readonly partNumber: number;
  readonly url: string;
}

export interface MultipartStorageUploadDestination {
  readonly contentType: string;
  readonly mediaId: string;
  readonly partSize: number;
  readonly parts: readonly MultipartStorageUploadPart[];
  readonly rendition: MediaRendition;
  readonly strategy: "multipart";
  readonly uploadId: string;
}

export type StorageUploadDestination =
  | SingleStorageUploadDestination
  | MultipartStorageUploadDestination;

export interface CompletedStorageUploadPart {
  readonly etag: string;
  readonly partNumber: number;
}

export function usesMultipartStorageUpload(
  byteLength: number,
  rendition: MediaRendition,
): boolean {
  return rendition === "original"
    && byteLength > STORAGE_MULTIPART_THRESHOLD_BYTES;
}

export function storageMultipartPartCount(
  byteLength: number,
  partSize = STORAGE_MULTIPART_PART_BYTES,
): number {
  if (
    !Number.isSafeInteger(byteLength)
    || byteLength < 1
    || !Number.isSafeInteger(partSize)
    || partSize < 5 * 1024 * 1024
  ) {
    throw new Error("Multipart upload dimensions are invalid.");
  }
  return Math.ceil(byteLength / partSize);
}

export function parseStorageUploadDestinations(
  value: unknown,
): readonly StorageUploadDestination[] {
  if (!Array.isArray(value)) {
    throw new Error("The storage upload destinations are unavailable.");
  }
  const destinations = value.map(parseStorageUploadDestination);
  if (new Set(destinations.map((destination) => (
    `${destination.mediaId}:${destination.rendition}`
  ))).size !== destinations.length) {
    throw new Error("The storage upload destinations are invalid.");
  }
  return destinations;
}

function parseStorageUploadDestination(
  value: unknown,
): StorageUploadDestination {
  if (
    !isRecord(value)
    || typeof value.contentType !== "string"
    || typeof value.mediaId !== "string"
    || !isMediaRendition(value.rendition)
  ) {
    throw new Error("The storage upload destination is invalid.");
  }
  if (value.strategy === "single" && typeof value.url === "string") {
    return {
      contentType: value.contentType,
      mediaId: value.mediaId,
      rendition: value.rendition,
      strategy: "single",
      url: value.url,
    };
  }
  if (
    value.strategy !== "multipart"
    || typeof value.uploadId !== "string"
    || !Number.isSafeInteger(value.partSize)
    || Number(value.partSize) < 5 * 1024 * 1024
    || !Array.isArray(value.parts)
  ) {
    throw new Error("The storage upload destination is invalid.");
  }
  const parts = value.parts.map((part, index) => {
    if (
      !isRecord(part)
      || part.partNumber !== index + 1
      || typeof part.url !== "string"
    ) {
      throw new Error("The multipart upload destination is invalid.");
    }
    return { partNumber: index + 1, url: part.url };
  });
  if (parts.length < 1 || parts.length > 10_000) {
    throw new Error("The multipart upload destination is invalid.");
  }
  return {
    contentType: value.contentType,
    mediaId: value.mediaId,
    partSize: Number(value.partSize),
    parts,
    rendition: value.rendition,
    strategy: "multipart",
    uploadId: value.uploadId,
  };
}

function isMediaRendition(value: unknown): value is MediaRendition {
  return value === "thumbnail" || value === "preview" || value === "original";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
